import { CSSProperties, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Button, ModalButtonWithForm } from 'components/common';
import { UsersListSelectedUsers } from '.';

import type { IconProp } from 'components/common/icons/Icon';
import type { UpdateUserObject } from 'components/users/Users';
import type { SelectedUser } from 'components/users/UsersListSelectedUsers';

type ChoiceProps = {
  icon: IconProp;
  iconStyle?: CSSProperties;
  style: CSSProperties;
};

interface Props {
  handleSuccess: (updateUser: UpdateUserObject) => Promise<void>;
}

const UsersLockInBatch = ({ handleSuccess }: Props) => {
  const { dispatch } = useDispatch();

  const { messages, theme, user: loggedInUser } = useReduxState();

  const [locked, setLocked] = useState(true);
  const [users, setUsers] = useState<SelectedUser[]>([]);
  const [visible, setVisible] = useState(false);

  const submit = async () => {
    try {
      const response = await api.put('/admin/users-update-in-batch', {
        ids: users.map((user) => user.id),
        locked
      });

      await handleSuccess({});

      dispatch(
        actions.addNotice({
          text: getMessageWithPlaceholders({
            message: locked
              ? messages.usersLockAccountLockSuccess
              : messages.usersLockAccountUnlockSuccess,
            placeholders: {
              numberOfAccounts: response.data.numberOfModifiedUsers
            }
          }),
          type: 'success'
        })
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const activeChoiceProps: ChoiceProps = {
    icon: 'check-circle',
    style: {
      backgroundColor: theme.lightGreyDarkActive,
      cursor: 'default',
      pointerEvents: 'none'
    }
  };

  const inactiveChoiceProps: ChoiceProps = {
    icon: 'circle',
    iconStyle: { opacity: 0.5 },
    style: { backgroundColor: theme.lightGrey }
  };

  return (
    <ModalButtonWithForm
      buttonIcon="lock"
      buttonIsDropdown={true}
      buttonStyle={{ whiteSpace: 'nowrap' }}
      buttonText={messages.usersLockAccountManage}
      modalActionText={messages.commonConfirm}
      modalTitle={messages.usersLockAccountManage}
      modalWidth="wide"
      onHide={() => setVisible(false)}
      onShow={() => setVisible(true)}
      onSubmit={submit}
    >
      {() => (
        <>
          {visible ? (
            <UsersListSelectedUsers
              includeAdmins={loggedInUser?.roles.includes('superadmin')}
              onLoadedUsers={(users) => setUsers(users)}
            />
          ) : null}

          <div className="mode-container">
            <Button
              category="light"
              onClick={() => setLocked(true)}
              text={messages.usersLockAccount}
              {...(locked ? activeChoiceProps : inactiveChoiceProps)}
            />
            <Button
              category="light"
              onClick={() => setLocked(false)}
              text={messages.usersUnlockAccount}
              {...(!locked ? activeChoiceProps : inactiveChoiceProps)}
            />

            <style jsx>{`
              .mode-container {
                display: flex;
                flex-direction: row;
                gap: 30px;
                margin-top: 20px;
                padding-bottom: 10px;
              }
            `}</style>
          </div>
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default UsersLockInBatch;
